import type { ApiPromise } from "@polkadot/api"
import type { QueryFunctionContext } from "@tanstack/query-core"
import { isRight } from "fp-ts/lib/Either"

import * as R from 'ramda'
import { getOneChainDisplayName } from "./requests"

const hexToUtf8 = (hex: string): string => {
  if (hex.startsWith('0x')) {
    hex = hex.slice(2)
  }
  return decodeURIComponent(hex.replace(/[0-9a-f]{2}/g, '%$&'));
}

export interface ChainIndentity {
  displayName?: string
}

export function queryChainIdentity(api: ApiPromise, address: string) {
  return {
    queryKey: ['onChainIdentity', address],
    queryFn: async (ctx: any) => {
      const {queryKey: [, address]} = ctx as QueryFunctionContext<[string, string]>
      const result = await api.query.identity.identityOf(address)
      if (result.isSome) {
        const raw = result.unwrap().toJSON() as {info: {display: {raw: string}}}
        return {
          displayName: hexToUtf8(raw.info.display.raw)
        } as ChainIndentity
      }
      return {
        displayName: undefined,
      } as ChainIndentity
    }
  }
}

export function queryMultiChainIdentity(api: ApiPromise, addresses: string[]) {
  return {
    queryKey: ['onChainIdentityBulk', addresses],
    queryFn: async (ctx: any) => {
      const {queryKey: [, addresses]} = ctx as QueryFunctionContext<[string, string[]]>
      const result = await api.query.identity.identityOf.multi(addresses)
      return R.map(
        i => {
          if (i.isSome) {
            const raw = i.unwrap().toJSON() as {info: {display: {raw: string}}}
            return {
              displayName: hexToUtf8(raw.info.display.raw)
            } as ChainIndentity
          }
          return {
            displayName: undefined,
          } as ChainIndentity
        },
        result
      )
    }
  }
}

export function queryIndexedChainIdentity(endpoint: string, address?: string) {
  return {
    queryKey: ['indexedChainIdentity', endpoint, address],
    queryFn: async (ctx: any) => {
      const { queryKey: [, endpoint, address] } = ctx as QueryFunctionContext<[
        string, string, string | undefined | null
      ]>
      if (address) {
        const result = await getOneChainDisplayName(endpoint, address)
        if (isRight(result)) {
          return {displayName: result.right || null }
        }
      }
      return { displayName: null }
    }
  }
}